"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const renames_1 = require("../../utilities/renames");
const UnexpectedError_1 = require("../../exception/UnexpectedError");
const Customer_1 = require("../domain/Customer");
class CustomerService {
    userRepository;
    authRepository;
    customerBusinessRepository;
    recommendationRepository;
    constructor(userRepository, authRepository, customerBusinessRepository, recommendationRepository) {
        this.userRepository = userRepository;
        this.authRepository = authRepository;
        this.customerBusinessRepository = customerBusinessRepository;
        this.recommendationRepository = recommendationRepository;
    }
    removeEmptyFromObj(obj) {
        Object.keys(obj).forEach((key) => {
            if (obj[key] && typeof obj[key] === 'object') {
                this.removeEmptyFromObj(obj[key]);
                if (Object.keys(obj[key]).length === 0) {
                    delete obj[key];
                }
            }
            else if (obj[key] == null ||
                obj[key] === '' ||
                (Array.isArray(obj[key]) && obj[key].length === 0)) {
                delete obj[key];
            }
            else if (Array.isArray(obj[key]) && obj[key].length > 0) {
                this.removeEmptyFromObj(obj[key]);
            }
        });
        return obj;
    }
    transformDataRepresentative(data) {
        let representative = {
            id: data?.id,
            type: data?.type,
            roles: data?.roles,
            name: data?.name,
            middleName: data?.middleName || undefined,
            lastName: data?.lastName,
            motherLastName: data?.motherLastName,
            identityDocuments: [
                {
                    type: data?.documentType,
                    number: data?.documentValue,
                },
            ],
            residenceCountry: data?.residenceCountry,
            address: {
                street: data?.addressStreet,
                location: {
                    level1: data?.addressLevel1,
                    level2: data?.addressLevel2,
                    level3: data?.addressLevel3,
                },
            },
            email: data?.email,
            landline: data?.landline,
            mobile: data?.mobileCountryCode && data?.mobile
                ? `${data?.mobileCountryCode}${data?.mobile}`
                : undefined,
            birthCountry: data?.birthCountry,
            birthday: data?.birthday,
            nationality: data?.nationality,
            education: {
                profession: data?.educationProfession,
            },
            employment: {
                employer: data?.employmentEmployer || undefined,
                position: data?.employmentPosition || undefined,
                startYear: data?.employmentStartYear || undefined,
            },
            taxes: {
                country: data?.taxesCountry || undefined,
            },
            pep: {
                status: data?.pepActive ? 'ACTIVE' : 'INACTIVE',
                entity: data?.pepEntity,
                position: data?.pepPosition,
            },
            canBuy: data?.canBuy,
            canSell: data?.canSell,
            isSignatory: data?.isSignatory,
            shareOwnership: data?.shareOwnership,
            metadata: {
                'vinculacion.position': undefined,
                'vinculacion.startYear': undefined,
                'vinculacion.relation': data?.vinculacionRelation,
                'vinculacion.rights': data?.vinculacionRights,
                'vinculacion.rightsNumber': data?.vinculacionRightsNumber,
                'vinculacion.rightsNominalValue': data?.vinculacionRightsNominalValue,
                'vinculacion.custodyCountry': data?.vinculacionCustodyCountry,
            },
        };
        let files = {};
        if (data.documentoContacto) {
            representative.attachments = {
                documentoContacto: {
                    contentType: data.documentoContacto?.type,
                },
            };
            files.documentoContacto = data.documentoContacto;
        }
        representative = this.removeEmptyFromObj(representative);
        return { representative, files };
    }
    async createCustomer({ customer, password, participantId, }) {
        try {
            await this.authRepository.signUp(customer, password, participantId);
            return {
                email: customer.email,
            };
        }
        catch (e) {
            if (e.code === 'UserLambdaValidationException') {
                throw new Error('Código de referidor inválido');
            }
            if (e.code === 'UsernameExistsException') {
                throw new Error('Cuenta ya registrada');
            }
            throw new UnexpectedError_1.UnexpectedError();
        }
    }
    async resendCodeSignUp(email) {
        await this.authRepository.resendCodeSignUp(email);
    }
    async signIn({ email, captcha, password, }) {
        let response;
        try {
            response = await this.authRepository.signIn(email, captcha, password);
        }
        catch (e) {
            if (e.code === 'NotAuthorizedException' ||
                e.code === 'UserNotFoundException') {
                throw new Error('Correo o contraseña incorrecta');
            }
            if (e.code === 'UserNotConfirmedException') {
                throw new Error('Correo electrónico no ha sido confirmado.');
            }
            throw new UnexpectedError_1.UnexpectedError();
        }
        return response;
    }
    async signOut() {
        await this.authRepository.signOut();
    }
    async getCurrentCustomer(querys) {
        const { payload } = (await this.authRepository.currentSession()).getIdToken();
        return await this.userRepository.getCustomer(payload.sub, querys);
    }
    async getCustomer(customerId, querys) {
        return this.userRepository.getCustomer(customerId, querys);
    }
    async confirmSignUp({ email, code, }) {
        try {
            await this.authRepository.confirmSignUp(email, code);
        }
        catch (e) {
            if (e.code === 'CodeMismatchException') {
                throw new Error('Código incorrecto');
            }
            throw new UnexpectedError_1.UnexpectedError();
        }
    }
    async forgotPassword(email) {
        try {
            await this.authRepository.forgotPassword(email);
        }
        catch (e) {
            if (e.code === 'UserNotFoundException') {
                throw new Error('Correo inválido');
            }
            if (e.message === 'Attempt limit exceeded, please try after some time.') {
                throw new Error('Llegaste al límite de intentos');
            }
            throw new UnexpectedError_1.UnexpectedError();
        }
    }
    async verifyOtpForgotPassword({ email, code, password, }) {
        try {
            await this.authRepository.forgotPasswordSubmit(email, code, password);
        }
        catch (e) {
            if (e.code === 'CodeMismatchException') {
                throw new Error('Código incorrecto');
            }
            throw new UnexpectedError_1.UnexpectedError();
        }
    }
    adaptUpdateCustomerBusiness(customer) {
        return {
            id: customer.id,
            type: Customer_1.CustomerType.BUSINESS,
            name: customer.name,
            identityDocuments: customer.documentValue
                ? [
                    {
                        type: Customer_1.IdentityDocumentType.RUC,
                        number: customer.documentValue ?? undefined,
                    },
                ]
                : undefined,
            country: customer.country,
            status: customer.status,
            lineOfBusiness: customer.lineOfBusiness,
            mobile: customer.mobile,
            email: customer.email,
            riskProfile: customer.riskProfile,
            address: {
                street: customer.addressStreet,
                location: {
                    level1: customer.department,
                    level2: customer.province,
                    level3: customer.district,
                },
            },
            statements: customer.statements,
            signModalities: customer.signModalities || [],
            metadata: {
                partidaNumero: customer['metadata.partidaNumero'],
                zonaRegistral: customer['metadata.zonaRegistral'],
                'fondoInversion.sociedadAdministradora': customer['metadata.fondoInversion.sociedadAdministradora'],
                'fondoInversion.ruc': customer['metadata.fondoInversion.ruc'],
            },
            ...(customer.attachments && {
                attachments: Object.fromEntries(Object.entries(customer.attachments).map(([fileName, file]) => [
                    fileName,
                    { contentType: file?.type },
                ])),
            }),
        };
    }
    async updateCustomer(customer) {
        let _customer;
        let signModalities = [];
        if (customer.type === Customer_1.CustomerType.BUSINESS) {
            _customer = this.adaptUpdateCustomerBusiness(customer);
            signModalities = customer.signModalities || [];
        }
        if (customer.type === Customer_1.CustomerType.INDIVIDUAL) {
            _customer = customer;
        }
        await this.userRepository.updateCustomer({
            ...this.removeEmptyFromObj(_customer),
            signModalities,
        }, customer.attachments);
    }
    async getPayload(params) {
        if (params?.updatedPayload) {
            const payload = await this.authRepository.getUpdatedPayload();
            return payload;
        }
        const { payload } = (await this.authRepository.currentSession()).getIdToken();
        return payload;
    }
    async changePassword({ oldPassword, newPassword, }) {
        try {
            await this.authRepository.changePassword(oldPassword, newPassword);
        }
        catch (e) {
            if (e.code === 'NotAuthorizedException') {
                throw new Error('Contraseña equivocada');
            }
            if (e.code === 'LimitExceededException') {
                throw new Error('Llegaste al límite de intentos');
            }
            throw new UnexpectedError_1.UnexpectedError();
        }
    }
    async createOtp(params) {
        return this.userRepository.createOtp(params.customer, params.type);
    }
    async verifyOtp(params) {
        try {
            await this.userRepository.verifyOtp(params.customer, params.otpId, params.code);
        }
        catch (error) {
            if (error?.response?.data?.message === 'Invalid code') {
                throw new Error('Codigo invalido');
            }
            if (error?.response?.data?.message === 'Otp can no longer be updated') {
                throw new Error('code_expired_or_used');
            }
            throw new Error(error);
        }
    }
    async getRecommendedFunds(params) {
        const response = await this.recommendationRepository.getRecommendedFunds(params);
        return response.map((f) => ({
            ...f,
            name: (0, renames_1.getFundName)(f?.id),
        }));
    }
    async getRecommendedSeries(params) {
        const response = await this.recommendationRepository.getRecommendedSeries(params);
        return response.map((item) => ({
            series: item.series,
            name: (0, renames_1.getSeriesName)(item.series),
            recommended: item.recommended,
        }));
    }
    async getCustomerBusinessDetails(customerId, types) {
        const response = await this.customerBusinessRepository.getCustomerBusinessDetails(customerId, types);
        return response;
    }
    async createRepresentative(params) {
        let { representative, files } = this.transformDataRepresentative(params.data);
        return this.customerBusinessRepository.createRepresentative(params.customerId, representative, files);
    }
    async updateRepresentative(params) {
        let { representative, files } = this.transformDataRepresentative(params.data);
        return this.customerBusinessRepository.updateRepresentative(params.customerId, representative, files);
    }
    async deleteRepresentative(params) {
        return this.customerBusinessRepository.deleteRepresentative(params.customerId, params.representativeId);
    }
}
exports.default = CustomerService;
//# sourceMappingURL=CustomerServiceImpl.js.map